/**
 * Update Actions - Snippet editing with UI integration
 *
 * This action handles snippet updates with full UI integration:
 * - Updates the snippet via the API
 * - Shows success/error flags
 * - Updates the snippet in the editor's quick insert menu
 * - Updates the snippet in the store in place (preserves list order)
 *
 * Separated from fetchActions because it has UI dependencies (flags, intl, editor API)
 * that the pure data operations don't need.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { editSnippetModalMessages } from '../../modals/EditSnippetModal.i18n.ts';
import { updateSnippetInQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import type { UpdateSnippetPayload } from '../../services/types/blockServiceTypes.ts';
import type { Snippet } from '../../types/snippet.ts';
import { reportError } from '../../utils/reportError.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { showSnippetErrorFlag, showSnippetSuccessFlag } from '../../utils/snippetFlags.ts';

import { shareActions } from './shareActions.ts';
import { getSnippetErrorFlagMessages } from './actionsHelper.ts';

/**
 * Options for updateSnippet that enable UI feedback and editor integration
 */
export type UpdateSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API for updating snippet in quick insert (passed from the caller, e.g. from the modal's props) */
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	/** Form value: when site sharing is enabled, share or unshare to match this value after update. Caller must pass so the action can decide share/unshare without reading store. */
	isShared: boolean;
	/** Previous isShared (from snippet when modal opened). Caller must pass with isShared so the action can decide share/unshare. */
	previousIsShared: boolean;
	/** Called when share API fails after update (e.g. for analytics) */
	onShareFailure?: (snippetId: string, error: Error) => void;
	/** Called when unshare API fails after update (e.g. for analytics) */
	onUnshareFailure?: (snippetId: string, error: Error) => void;
	/** Called when the update API itself fails; receives the thrown error */
	onSaveFailure?: (error: Error) => void;
};

/**
 * Actions for updating snippets with UI integration.
 */
export const updateActions = {
	/**
	 * Updates an existing snippet and updates the store in place (preserves list order).
	 *
	 * On success:
	 * - Replaces the snippet in the store with the API response
	 * - Shows a success flag (if options provided)
	 * - Adds/updates the snippet in the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Keeps existing snippets visible (doesn't reset isLoaded)
	 *
	 * @param snippetId - The ID of the snippet to update
	 * @param name - The name of the snippet
	 * @param body - The body of the snippet
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API
	 * @returns Promise resolving to the updated snippet, or null on failure
	 */
	updateSnippet:
		(
			snippetId: string,
			name: string,
			body: ADFEntity,
			description?: string,
			options?: UpdateSnippetOptions,
		) =>
		async (storeApi: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const { getState, setState, dispatch } = storeApi;
			const descriptionValue = description?.trim();
			const params: UpdateSnippetPayload = {
				name,
				...(descriptionValue ? { description: descriptionValue } : {}),
				body: JSON.stringify(body),
			};

			try {
				const snippet = await BlockServiceClient.updateSnippet(snippetId, params);

				const currentSnippets = getState().snippets;
				const updatedSnippets = currentSnippets.map((s) =>
					s.snippetId === snippet.snippetId ? snippet : s,
				);

				setState({ snippets: updatedSnippets });

				if (options) {
					const { flags, intl, editorAPI, isShared } = options;
					showSnippetSuccessFlag(
						flags,
						intl.formatMessage(editSnippetModalMessages.editSuccessFlagTitle, {
							snippetName: snippet.name,
						}),
						'',
					);

					if (editorAPI) {
						updateSnippetInQuickInsertItem({ snippet, editorAPI });
					}
					if (getState().isSnippetSharingForSiteEnabled) {
						const hasSharedChanged = isShared != options.previousIsShared;
						const shareOrUnshare =
							isShared && !options.previousIsShared
								? shareActions.shareSnippet
								: shareActions.unshareSnippet;

						if (hasSharedChanged) {
							await dispatch(
								shareOrUnshare(snippetId, {
									flags,
									intl,
									editorAPI: editorAPI ?? undefined,
									onShareFailure: options.onShareFailure,
									onUnshareFailure: options.onUnshareFailure,
								}),
							);
						}
					}
				}
				return snippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));
				reportError(err);

				if (options) {
					options.onSaveFailure?.(err);
					const { flagTitle, flagDescription } = getSnippetErrorFlagMessages(
						err,
						editSnippetModalMessages.editErrorFlagTitle,
						editSnippetModalMessages.editErrorFlagDescription,
					);
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(flagTitle),
						options.intl.formatMessage(flagDescription),
					);
					return null;
				}
				throw err;
			}
		},
};
